import { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getSalesApi } from '../../../salesApi/salesApi'
import { TaskInterface } from '../../../common/types/interfaces'
import { TaskStatus } from '../../../common/types/enums'
import {
  selectTaskPool,
  selectMyTasks,
  selectSelectedTask,
  setSelectedTask,
  upsertTask,
} from '../../../store/slices/taskSlice'
import { selectExecutorClusters } from '../../../store/slices/executorSlice'
import { selectCurrentUser } from '../../../store/slices/currentUserSlice'

export const useTaskDetails = () => {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const salesApi = getSalesApi()

  const clusterId = Number(searchParams.get('clusterId'))
  const taskId = Number(id)

  const pool = useSelector(selectTaskPool)
  const my = useSelector(selectMyTasks)
  const selectedTask = useSelector(selectSelectedTask)
  const executorClusters = useSelector(selectExecutorClusters)
  const currentUser = useSelector(selectCurrentUser)

  const fromRedux =
    (selectedTask && selectedTask.id === taskId && selectedTask.clusterId === clusterId
      ? selectedTask
      : null) ||
    pool.find((t) => t.id === taskId && t.clusterId === clusterId) ||
    my.find((t) => t.id === taskId && t.clusterId === clusterId) ||
    null

  const [task, setTask] = useState<TaskInterface | null>(fromRedux)
  const [loading, setLoading] = useState(!fromRedux)
  const [error, setError] = useState<string | null>(null)

  const getCluster = useCallback(() => {
    return executorClusters.find((c) => c.cluster.id === clusterId)?.cluster
  }, [executorClusters, clusterId])

  useEffect(() => {
    if (task) return

    if (selectedTask && selectedTask.id === taskId && selectedTask.clusterId === clusterId) {
      setTask(selectedTask)
      return
    }

    const fromPool = pool.find((t) => t.id === taskId && t.clusterId === clusterId)
    const fromMy = my.find((t) => t.id === taskId && t.clusterId === clusterId)
    const found = fromPool || fromMy

    if (found) {
      setTask(found)
      dispatch(setSelectedTask(found))
    }
  }, [taskId, clusterId, pool, my, selectedTask, task, dispatch])

  useEffect(() => {
    const fetchTask = async () => {
      const cluster = getCluster()
      if (!cluster || !taskId) return

      setLoading(true)
      setError(null)

      try {
        salesApi.setClusterUrl(cluster)
        const response = await salesApi.getTask(taskId)

        if (response?.task) {
          const taskWithCluster = { ...response.task, cluster }
          setTask(taskWithCluster)
          dispatch(upsertTask(taskWithCluster))
          dispatch(setSelectedTask(taskWithCluster))
        }
      } catch (err: any) {
        console.error('Fetch task error:', err)
        if (!task) {
          setError(err?.response?.data?.error || 'Не удалось загрузить задание')
        }
      } finally {
        setLoading(false)
      }
    }

    if (currentUser?.id && executorClusters.length > 0) {
      fetchTask()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, clusterId, currentUser?.id, executorClusters.length])

  // ❌ Перезагружаем задачу с бекенда
  const refetchTask = useCallback(async () => {
    const cluster = getCluster()
    if (!cluster || !taskId) return

    try {
      salesApi.setClusterUrl(cluster)
      const response = await salesApi.getTask(taskId)

      if (response?.task) {
        const taskWithCluster = { ...response.task, cluster }
        setTask(taskWithCluster)
        dispatch(upsertTask(taskWithCluster))
        dispatch(setSelectedTask(taskWithCluster))
      }
    } catch (err) {
      console.error('Refetch task error:', err)
    }
  }, [taskId, getCluster, salesApi, dispatch])

  const takeTask = useCallback(async () => {
    const cluster = getCluster()
    if (!cluster || !task) return

    try {
      salesApi.setClusterUrl(cluster)
      await salesApi.takeTask(task.id)
      await refetchTask()
    } catch (err) {
      console.error('Take task error:', err)
    }
  }, [task, getCluster, salesApi, refetchTask])

  const updateStatus = useCallback(
    async (status: TaskStatus, comment?: string) => {
      const cluster = getCluster()
      if (!cluster || !task) return

      try {
        salesApi.setClusterUrl(cluster)
        await salesApi.updateTaskStatus(task.id, status, comment)
        await refetchTask()
      } catch (err) {
        console.error('Update task status error:', err)
      }
    },
    [task, getCluster, salesApi, refetchTask],
  )

  return {
    task,
    loading: task ? false : loading,
    error,
    takeTask,
    updateStatus,
  }
}
